import $ from 'jquery';

import {Table} from 'notebook/tables/constants/TableConstants';
import excess from 'notebook/tables/excess';
import {EvaluatedCell, Selector} from 'notebook/tables/excess/EvaluationConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import pick from 'util/pick';

/**
 * Convert a subset of a table into HTML, such that it can be pasted externally or internally (which handles
 * translating forumulas correctly when used in conjunction with wcywiwyp/parseTable)
 *
 * @param {object} table - of the shape TableConstants.table
 * @param {obect} computedTable - a 2D array of the shape TableConstants.evaluatedCell
 *   corresponding to `table`
 * @param {number} options.minI - min row index
 * @param {number} options.maxI - max row index (inclusive)
 * @param {number} options.minJ - min col index
 * @param {number} options.maxJ - max col index (inclusive)
 * @param {boolean} disableFormulas - whether or not cells should be interpreted as formula
 * @return {object} - a jQuery wrapped <table>
 */
export default function makeTableHTML(
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>,
  {minI, maxI, minJ, maxJ}: {minI: number; minJ: number; maxI: number; maxJ: number},
  {disableFormulas, showFormulas}: {disableFormulas?: boolean; showFormulas?: boolean} = {
    disableFormulas: false,
    showFormulas: false,
  }
): JQuery {
  let $thead: JQuery;
  const $table = $('<table></table>')
    .css('border-spacing', 0)
    .css('border-collapse', 'collapse')
    .attr('data-excess', '');
  const hasHeaderRow = minI === 0 && table.headerRow;
  const hasHeaderCol = minJ === 0 && table.headerCol;

  if (hasHeaderRow) {
    $thead = $('<thead></thead>');
    $table.append($thead);
  }
  const $tbody = $('<tbody></tbody>');
  $table.append($tbody);

  for (let i = minI; i <= maxI; i++) {
    const $tr = $('<tr></tr>');
    for (let j = minJ; j <= maxJ; j++) {
      // check if this cell should be skipped
      let formula;
      const evaluatedCell = computedTable[i][j];
      if (TableUtil.isNotRendered(i, j, evaluatedCell)) {
        continue;
      }

      // In some cases, text is not set, but there is a value set in the td.textContent below, which we'd
      // prefer to surface.
      if (disableFormulas || !table.table[i][j].text) {
        formula = null;
      } else {
        formula = excess.tokenizer.toRelativeFormula(table.table[i][j].text, {
          row: i,
          column: j,
        });
      }
      const isHeaderCell = (j === 0 && hasHeaderCol) || (i === 0 && hasHeaderRow);
      const $td = (isHeaderCell ? $('<th></th>') : $('<td></td>'))
        .text(
          showFormulas ? table.table[i][j].text : formatTypedValue(computedTable[i][j].value, evaluatedCell)
        )
        .css('border', '1px solid black')
        .css('padding', '0 10px');
      if (formula != null) {
        $td.attr('data-excess-formula', formula);
      }
      if (table.table[i][j].bold) {
        $td.css('font-weight', 'bold');
      }
      if (table.table[i][j].italic) {
        $td.css('font-style', 'italic');
      }
      const textDecorations = [];
      if (table.table[i][j].underline) {
        textDecorations.push('underline');
      }
      if (table.table[i][j].strikethrough) {
        textDecorations.push('line-through');
      }
      $td.css('text-decoration', textDecorations.join(' '));
      if (table.table[i][j].align) {
        $td.css('text-align', table.table[i][j].align!);
      }
      if (table.table[i][j].foregroundColor) {
        $td.css('color', table.table[i][j].foregroundColor!);
      }
      if (table.table[i][j].backgroundColor) {
        $td.css('background-color', table.table[i][j].backgroundColor!);
      }
      if (table.table[i][j].mergeRowSpan != null) {
        $td.attr('rowspan', table.table[i][j].mergeRowSpan!);
      }
      if (table.table[i][j].mergeColSpan != null) {
        $td.attr('colspan', table.table[i][j].mergeColSpan!);
      }
      if (table.table[i][j].link != null) {
        $td.attr('data-link', table.table[i][j].link!);
      }
      if (table.table[i][j].itemLinks != null) {
        $td.attr('data-item-links', JSON.stringify(table.table[i][j].itemLinks));
      }
      if (table.table[i][j].schemaFieldSelectorOptionIds != null) {
        $td.attr('data-sfs-options', JSON.stringify(table.table[i][j].schemaFieldSelectorOptionIds));
      }
      const typedValue = table.table[i][j].typedValue;
      if (typedValue != null) {
        $td.attr('data-typed-value', JSON.stringify(typedValue));
      }
      // @ts-expect-error
      const parentStorableSummary = table.table[i][j].parentStorableSummary;
      if (parentStorableSummary != null) {
        $td.attr('data-parent-storable-summary', JSON.stringify(parentStorableSummary));
      }
      // @ts-expect-error
      const batch = table.table[i][j].batch;
      if (batch != null) {
        $td.attr('data-batch', JSON.stringify(batch));
      }
      if (table.table[i][j].precision != null) {
        $td.attr('data-precision', table.table[i][j].precision!);
      }
      if (table.table[i][j].important) {
        $td.attr('data-important', '1');
      }
      $td.attr('data-external-cache', JSON.stringify(table.table[i][j].externalCache));
      if (computedTable[i][j].selector != null) {
        const selector = pick(computedTable[i][j].selector! as Selector, 'name', 'options', 'staticId');
        $td.attr('data-selector', JSON.stringify(selector));
      }
      $tr.append($td);
    }

    if (i === 0 && hasHeaderRow) {
      $thead!.append($tr);
    } else {
      $tbody.append($tr);
    }
  }
  $table.toggleClass('benchling-header-row', hasHeaderRow!);
  $table.toggleClass('benchling-header-col', hasHeaderCol!);
  return $table;
}
